import { useCallback, useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Bell, CheckCheck } from "lucide-react";
import { api } from "@/lib/api";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";

const DOT = { info: "bg-sky-500", warn: "bg-amber-500", danger: "bg-rose-500" };

const AlertRow = ({ it, onOpen }) => (
  <li>
    <button onClick={() => onOpen(it)} className={`flex w-full gap-2.5 px-3 py-2 text-left transition-colors hover:bg-sky-50 ${it.read ? "opacity-60" : ""}`} data-testid={`alert-item-${it.key}`}>
      <span className={`mt-1.5 h-2 w-2 shrink-0 rounded-full ${DOT[it.severity]}`} />
      <span className="min-w-0 flex-1">
        <span className="block truncate text-xs font-semibold text-slate-800">{it.title}</span>
        <span className="block truncate text-[11px] text-slate-500">{it.detail}</span>
      </span>
      {it.days > 0 && <span className="shrink-0 text-[10px] text-slate-400">{it.days}d</span>}
    </button>
  </li>
);

const Section = ({ title, items, onOpen, testId }) => (items.length > 0 && (
  <div data-testid={testId}>
    <p className="bg-slate-50 px-3 py-1 text-[10px] font-semibold uppercase tracking-wider text-slate-500">{title} · {items.length}</p>
    <ul className="divide-y divide-slate-100">{items.map((it) => <AlertRow key={it.key} it={it} onOpen={onOpen} />)}</ul>
  </div>
));

export const NotificationBell = () => {
  const nav = useNavigate();
  const { pathname } = useLocation();
  const [data, setData] = useState({ items: [], unread: 0 });
  const [open, setOpen] = useState(false);

  const load = useCallback(() => api.get("/alerts").then((r) => setData(r.data)).catch(() => {}), []);
  useEffect(() => { load(); }, [load, pathname]);
  useEffect(() => {
    const t = setInterval(load, 60000);
    return () => clearInterval(t);
  }, [load]);

  const markRead = async (keys) => {
    if (!keys.length) return;
    await api.post("/alerts/read", { keys });
    load();
  };
  const openItem = (it) => {
    setOpen(false);
    markRead(it.read ? [] : [it.key]);
    nav(it.link);
  };
  const unreadKeys = data.items.filter((i) => !i.read).map((i) => i.key);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button className="relative flex h-8 w-8 items-center justify-center rounded-full border border-slate-200 bg-white text-slate-600 transition-colors hover:bg-sky-50 hover:text-sky-700" aria-label="Notifications" data-testid="notification-bell">
          <Bell className="h-4 w-4" />
          {data.unread > 0 && (
            <span className="absolute -right-1 -top-1 flex h-4 min-w-4 items-center justify-center rounded-full bg-rose-500 px-1 text-[9px] font-bold text-white" data-testid="notification-count">{data.unread > 99 ? "99+" : data.unread}</span>
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" collisionPadding={8} className="w-[340px] max-w-[92vw] overflow-hidden rounded-xl p-0" data-testid="notification-panel">
        <div className="flex items-center justify-between border-b border-slate-100 px-3 py-2.5">
          <div>
            <p className="text-sm font-semibold text-slate-900">Notifications</p>
            <p className="text-[11px] text-slate-500">{data.unread} unread · approvals & alerts for you</p>
          </div>
          <button onClick={() => markRead(unreadKeys)} disabled={!unreadKeys.length} className="flex items-center gap-1 rounded-md px-2 py-1 text-[11px] font-medium text-sky-700 hover:bg-sky-50 disabled:opacity-40" data-testid="notification-mark-all">
            <CheckCheck className="h-3.5 w-3.5" />Mark all read
          </button>
        </div>
        <div className="max-h-[420px] overflow-y-auto">
          <Section title="Pending your approval" items={data.items.filter((i) => i.kind === "approval")} onOpen={openItem} testId="notification-approvals" />
          <Section title="Alerts" items={data.items.filter((i) => i.kind === "alert")} onOpen={openItem} testId="notification-alerts" />
          {!data.items.length && <p className="px-3 py-8 text-center text-xs text-slate-500" data-testid="notification-empty">You're all caught up</p>}
        </div>
      </PopoverContent>
    </Popover>
  );
};
